import axios from 'axios';
import { getAccessToken, getRefreshToken, clearAccessToken, setRefreshToken } from '../utils/authToken';

// --- Base URL Configuration ---
const PROD_BASE_URL = "https://port-0-bemaster-mild533144fe3281.sel3.cloudtype.app";

// Dev에서는 CRA proxy를 쓰기 위해 same-origin(상대경로)로 호출
export const MAIN_BASE_URL =
    process.env.REACT_APP_MAIN_BASE_URL ||
    (process.env.NODE_ENV === 'development' ? '' : PROD_BASE_URL);

export const SEARCH_BASE_URL =
    process.env.REACT_APP_SEARCH_BASE_URL ||
    (process.env.NODE_ENV === 'development' ? '' : PROD_BASE_URL);

// OAuth/browser navigation should NOT use CRA proxy (historyApiFallback can intercept).
// Always use an absolute backend origin (override with env if needed).
export const OAUTH_BASE_URL =
    process.env.REACT_APP_OAUTH_BASE_URL ||
    process.env.REACT_APP_MAIN_BASE_URL ||
    PROD_BASE_URL;

// --- Axios Instance Creator to share interceptor logic ---
const createAxiosInstance = (baseUrl) => {
    const instance = axios.create({
        baseURL: baseUrl,
    });

    // Request interceptor to add the auth token
    instance.interceptors.request.use(
        (config) => {
            // If we're sending FormData, let the browser set the multipart boundary.
            // Our instance default is application/json, which would break file uploads.
            if (config?.data instanceof FormData) {
                if (config.headers) {
                    // Axios v1 uses AxiosHeaders which supports .set
                    if (typeof config.headers.set === 'function') {
                        config.headers.set('Content-Type', undefined);
                    } else {
                        delete config.headers['Content-Type'];
                        delete config.headers['content-type'];
                    }
                }
            }

            // Exclude token for login and signup endpoints if needed, 
            // though usually sending it doesn't hurt unless server complains.
            // We'll keep the check for safety if it's the main API.
            if (config.baseURL === MAIN_BASE_URL && (config.url.endsWith('/login') || config.url.endsWith('/signup'))) {
                return config;
            }

            const token = getAccessToken();
            if (token) config.headers['Authorization'] = token;
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor - 401 에러 시 자동 토큰 갱신
    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // 401 에러이고 재시도가 아닌 경우
            if (error.response?.status === 401 && !originalRequest._retry) {
                // 로그인/리프레시 요청 자체가 실패한 경우는 재시도하지 않음
                if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/refresh')) {
                    return Promise.reject(error);
                }

                originalRequest._retry = true;

                const refreshToken = getRefreshToken();
                if (refreshToken) {
                    try {
                        console.log('Attempting to refresh access token...');
                        const res = await axios.post(`${baseUrl}/api/user/refresh`, { refreshToken });

                        const newAccessToken = res.data.accessToken;
                        const newRefreshToken = res.data.refreshToken;

                        // 새 토큰 저장
                        if (newAccessToken) {
                            const normalized = newAccessToken.startsWith('Bearer ')
                                ? newAccessToken
                                : `Bearer ${newAccessToken}`;
                            localStorage.setItem('accessToken', normalized);
                        }
                        if (newRefreshToken) {
                            setRefreshToken(newRefreshToken);
                        }

                        console.log('Token refresh successful, retrying original request...');

                        // 원래 요청 재시도
                        originalRequest.headers.Authorization = newAccessToken.startsWith('Bearer ')
                            ? newAccessToken
                            : `Bearer ${newAccessToken}`;
                        return instance(originalRequest);
                    } catch (refreshError) {
                        // Refresh Token도 만료됨 → 로그아웃 처리
                        console.error('Token refresh failed:', refreshError);
                        clearAccessToken();

                        if (!window.location.pathname.startsWith('/login')) {
                            alert("세션이 만료되었습니다. 다시 로그인해주세요.");
                            window.location.href = '/login';
                        }
                        return Promise.reject(refreshError);
                    }
                } else {
                    // Refresh Token이 없는 경우
                    clearAccessToken();
                    if (!window.location.pathname.startsWith('/login')) {
                        alert("세션이 만료되었습니다. 다시 로그인해주세요.");
                        window.location.href = '/login';
                    }
                }
            }
            return Promise.reject(error);
        }
    );

    return instance;
};

// --- API Instances ---
const api = createAxiosInstance(MAIN_BASE_URL); // Main User/Chat API
export const searchApi = createAxiosInstance(SEARCH_BASE_URL); // Search/AI API

// --- User/Auth API Methods ---
export const signup = async (username, password) => {
    try {
        const response = await api.post('/api/user/signup', { username, password });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const login = async (username, password) => {
    try {
        const response = await api.post('/api/user/login', { username, password });
        return response; // Returns full response for status check
    } catch (error) {
        throw error;
    }
};

export const logout = async () => {
    try {
        // Refresh Token이 있으면 함께 전송하여 서버에서 삭제
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await api.post('/api/user/logout',
            refreshToken ? { refreshToken } : {}
        );
        return response.data;
    } catch (error) {
        console.error("Logout failed:", error);
        throw error;
    }
};

export const getProfile = async () => {
    try {
        const response = await api.get('/api/user/profile');
        return response.data;
    } catch (error) {
        console.error("Failed to fetch profile:", error);
        throw error;
    }
};

export const updateProfile = async (profileData) => {
    try {
        const response = await api.put('/api/user/profile', profileData);
        return response.data;
    } catch (error) {
        console.error("Failed to update profile:", error);
        throw error;
    }
};

export const deleteAccount = async () => {
    const candidates = [
        '/api/user',
        '/api/user/me',
        '/api/users/me',
        '/api/user/withdraw',
        '/api/user/delete',
    ];

    let lastError;
    for (const url of candidates) {
        try {
            const response = await api.delete(url);
            return response.data;
        } catch (error) {
            lastError = error;
            const status = error?.response?.status;
            // 404인 경우에만 다음 후보로 재시도 (권한/검증 에러는 그대로 노출)
            if (status === 404) continue;
            console.error("Failed to delete account:", error);
            throw error;
        }
    }

    console.error("Failed to delete account:", lastError);
    throw lastError;
};

export const uploadProfileImage = async (file) => {
    try {
        const token = getAccessToken();
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${MAIN_BASE_URL}/api/user/profile/image`, {
            method: 'POST',
            headers: {
                'Authorization': token,
            },
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Image upload failed');
        }

        const json = await response.json();
        return json; // { profileImageUrl: "..." }
    } catch (error) {
        console.error("Failed to upload profile image:", error);
        throw error;
    }
};

// --- Email Verification ---
// (1) 인증 메일 발송 (로그인 필요)
export const sendEmailVerification = async (accessToken) => {
    try {
        // 가입 직후엔 토큰을 로컬스토리지에 저장하지 않고도 호출할 수 있게 옵션 지원
        if (accessToken) {
            const response = await axios.post(
                `${MAIN_BASE_URL}/api/user/email/verification/send`,
                null,
                {
                    headers: {
                        Authorization: accessToken,
                    },
                }
            );
            return response; // 204 No Content
        }

        // 기본: Authorization은 interceptor가 자동으로 붙임
        const response = await api.post('/api/user/email/verification/send');
        return response; // 204 No Content
    } catch (error) {
        throw error;
    }
};

// (2) 이메일 인증 확정 (로그인 불필요)
export const confirmEmailVerification = async (token) => {
    try {
        const response = await axios.post(
            `${MAIN_BASE_URL}/api/user/email/verification/confirm`,
            { token },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data; // { message: "..." }
    } catch (error) {
        throw error;
    }
};

export const getFavorites = async () => {
    try {
        const response = await api.get('/api/favorites');
        return response.data;
    } catch (error) {
        console.error("Failed to fetch favorites:", error);
        throw error;
    }
};

export const addFavorite = async (favoriteData) => {
    try {
        const response = await api.post('/api/favorites', favoriteData);
        return response.data;
    } catch (error) {
        console.error("Failed to add favorite:", error);
        throw error;
    }
};

export const deleteFavorite = async (favoriteId) => {
    try {
        const response = await api.delete(`/api/favorites/${favoriteId}`);
        return response.data;
    } catch (error) {
        console.error("Failed to delete favorite:", error);
        throw error;
    }
};

export default api;
